import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { MapPin, FileText, Calendar, Bell, Plus, Building, Hash, User, Trash2, ChevronRight, ChevronDown, AlertCircle, Clock, CheckCircle, AlertTriangle, Check, X } from 'lucide-react';
import AddStateWizard from '../components/AddStateWizard';
import AddLicenseDialog from '../components/AddLicenseDialog';
import AddRegistrationDialog from '../components/AddRegistrationDialog';
import AddRegisteredAgentDialog from '../components/AddRegisteredAgentDialog';
import EditLicenseDialog from '../components/EditLicenseDialog';
import EditDBADialog from '../components/EditDBADialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { api } from '../utils/api';
import { getStateAbbr, formatDate, getDaysUntil } from '../utils/helpers';
import { toast } from 'sonner';

const StateMatrix = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stateCardRefs = useRef({});
  
  // SINGLE SOURCE OF TRUTH: All state data comes from compliance endpoint
  const [complianceData, setComplianceData] = useState({});
  const [selectedState, setSelectedState] = useState('all');
  const [loading, setLoading] = useState(true);
  const [addStateDialogOpen, setAddStateDialogOpen] = useState(false);
  const [addLicenseDialogOpen, setAddLicenseDialogOpen] = useState(false);
  const [addRegistrationDialogOpen, setAddRegistrationDialogOpen] = useState(false);
  const [selectedStateForLicense, setSelectedStateForLicense] = useState(null);
  const [selectedStateForRegistration, setSelectedStateForRegistration] = useState(null);
  const [registeredAgentData, setRegisteredAgentData] = useState({});
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedStateForAgent, setSelectedStateForAgent] = useState(null);
  const [editLicenseDialogOpen, setEditLicenseDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [editDBADialogOpen, setEditDBADialogOpen] = useState(false);
  const [selectedDBA, setSelectedDBA] = useState(null);
  const [openStates, setOpenStates] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null
  });

  // State code to full name mapping
  const stateCodeToName = {
    'FL': 'Florida', 'TX': 'Texas', 'GA': 'Georgia', 'CA': 'California', 
    'NV': 'Nevada', 'DE': 'Delaware', 'AZ': 'Arizona', 'DC': 'District of Columbia',
    'NC': 'North Carolina', 'MI': 'Michigan', 'MO': 'Missouri', 'LA': 'Louisiana', 'VA': 'Virginia'
  };

  // State name to code mapping (inverted)
  const stateNameToCode = Object.entries(stateCodeToName).reduce((acc, [code, name]) => {
    acc[name] = code;
    return acc;
  }, {});

  useEffect(() => {
    loadRequirements();
    
    // Check if navigated from state detail panel with a state code
    if (location.state?.stateCode) {
      const stateName = stateCodeToName[location.state.stateCode];
      if (stateName) {
        setSelectedState(stateName);
      }
    }
  }, [location]);

  // Get list of states from compliance data
  const getStateList = () => {
    return Object.keys(complianceData)
      .map(code => stateCodeToName[code])
      .filter(Boolean)
      .sort();
  };

  const detailedStates = selectedState === 'all' 
    ? getStateList()
    : [selectedState];

  // Handle state highlighting from URL parameter (from heatmap)
  useEffect(() => {
    const stateCode = searchParams.get('state');
    if (stateCode && stateCodeToName[stateCode]) {
      const stateName = stateCodeToName[stateCode];
      setSelectedState(stateName);
      
      // Scroll to the state card after data loads
      setTimeout(() => {
        const cardRef = stateCardRefs.current[stateName];
        if (cardRef) {
          cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add highlight animation
          cardRef.classList.add('ring-4', 'ring-blue-500');
          setTimeout(() => {
            cardRef.classList.remove('ring-4', 'ring-blue-500');
          }, 2000);
        }
      }, 500);
    }
  }, [searchParams, complianceData]);

  const loadRequirements = async (preserveOpenStates = false) => {
    try {
      // SINGLE SOURCE OF TRUTH: Load all state compliance data from unified endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/state-compliance`);
      const data = await response.json();
      
      // Load registered agents data
      const raResponse = await api.getRAOverview();
      
      // Transform RA data to state-keyed format for easy lookup
      const raByState = {};
      if (raResponse?.assignments) {
        raResponse.assignments.forEach(assignment => {
          const stateName = stateCodeToName[assignment.state];
          if (stateName) {
            if (!raByState[stateName]) {
              raByState[stateName] = [];
            }
            raByState[stateName].push({
              agentName: assignment.registered_agent_name,
              agentType: 'Commercial Registered Agent',
              address: assignment.virtual_address,
              city: assignment.city,
              agentState: assignment.state_code,
              zip: assignment.zip,
              appointedDate: assignment.date_appointed || '',
              annualFee: assignment.annual_fee?.toFixed(2) || '0.00',
              provider_id: assignment.provider_id,
              assignment_id: assignment.assignment_id
            });
          }
        });
      }
      
      // Convert multi-RA states to single primary agent (first one)
      const primaryAgentByState = {};
      Object.keys(raByState).forEach(state => {
        primaryAgentByState[state] = raByState[state][0]; // Use first agent as primary
      });
      
      setRegisteredAgentData(primaryAgentByState);
      
      if (data.states) {
        // Store the full compliance data
        setComplianceData(data.states);
        
        // Extract state list for filtering
        const states = Object.keys(data.states).map(code => stateCodeToName[code]).filter(Boolean);
        
        // Only reset openStates on initial load, preserve them on refresh
        if (!preserveOpenStates) {
          // Initialize all states as collapsed
          const initialOpenStates = {};
          states.forEach(state => {
            initialOpenStates[state] = false;
          });
          setOpenStates(initialOpenStates);
        }
      }
    } catch (error) {
      console.error('Failed to load state compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // REMOVED: loadComplianceData() - now part of loadRequirements()
  // REMOVED: separate calls to api.getLicenses(), api.getDBAs(), api.getStateRequirements()

  // Helper functions now use compliance data directly (SINGLE SOURCE OF TRUTH)
  const getLicensesByState = (stateName) => {
    const stateCode = stateNameToCode[stateName];
    if (!stateCode || !complianceData[stateCode]) return [];
    return complianceData[stateCode].licenses || [];
  };

  const getRegistrationsByState = (stateName) => {
    const stateCode = stateNameToCode[stateName];
    if (!stateCode || !complianceData[stateCode]) return [];
    return complianceData[stateCode].registrations || [];
  };

  const getComplianceByState = (stateName) => {
    const stateCode = stateNameToCode[stateName];
    return complianceData[stateCode] || null;
  };

  const handleAddLicense = (state, e) => {
    e.stopPropagation();
    console.log('handleAddLicense called for state:', state);
    setSelectedStateForLicense(state);
    setAddLicenseDialogOpen(true);
    console.log('Dialog state set to true');
  };

  const handleAddRegistration = (state, e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedStateForRegistration(state);
    setAddRegistrationDialogOpen(true);
  };

  const handleAgentDateChange = (state, field, value) => {
    // Format as MM/DD/YYYY while typing
    let cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);
    }
    
    setRegisteredAgentData(prev => ({
      ...prev,
      [state]: { ...prev[state], [field]: cleaned }
    }));
  };

  const handleAddAgent = (state, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedStateForAgent(state);
    setAgentDialogOpen(true);
  };

  const handleSaveAgent = async (agentData) => {
    console.log('handleSaveAgent called with:', agentData);
    console.log('selectedStateForAgent:', selectedStateForAgent);
    
    try {
      // Step 1: Find or create provider
      let providerId = null;
      
      // Check if provider exists by name
      const providersResponse = await api.getRAProviders();
      const existingProvider = providersResponse.providers?.find(
        p => p.provider_name.toLowerCase() === agentData.agentName.toLowerCase()
      );
      
      if (existingProvider) {
        providerId = existingProvider.provider_id;
        console.log('Using existing provider:', providerId);
      } else {
        // Create new provider
        const providerPayload = {
          provider_name: agentData.agentName,
          contact_email: agentData.email || `contact@${agentData.agentName.toLowerCase().replace(/\s+/g, '')}.com`,
          contact_phone: agentData.phone || null,
          website_url: null,
          username: null,
          password: null
        };
        
        const newProvider = await api.createRAProvider(providerPayload);
        providerId = newProvider.provider_id;
        console.log('Created new provider:', providerId);
        toast.success(`Created new provider: ${agentData.agentName}`);
      }
      
      // Step 2: Get state code from state name
      const stateCode = Object.keys(stateCodeToName).find(
        code => stateCodeToName[code] === selectedStateForAgent
      );
      
      if (!stateCode) {
        throw new Error('Invalid state selected');
      }
      
      // Step 3: Check if state assignment already exists
      const stateAssignments = await api.getRAStateAssignments({ 
        provider_id: providerId, 
        state: stateCode 
      });
      
      const assignmentPayload = {
        provider_id: providerId,
        state: stateCode,
        entity_id: 'ENT-LOSSPAY-001',
        legal_entity_name: 'LossPay Risk Technologies, Inc.',
        registered_agent_name: agentData.agentName,
        virtual_address: agentData.address || null,
        city: agentData.city || null,
        state_code: agentData.agentState || stateCode,
        zip: agentData.zip || null,
        date_appointed: agentData.appointedDate ? new Date(convertToISODate(agentData.appointedDate)).toISOString() : null,
        annual_fee: agentData.fee ? parseFloat(agentData.fee) : (agentData.annualFee ? parseFloat(agentData.annualFee) : 0)
      };
      
      if (stateAssignments.assignments && stateAssignments.assignments.length > 0) {
        // Update existing assignment
        const assignmentId = stateAssignments.assignments[0].assignment_id;
        await api.updateRAStateAssignment(assignmentId, assignmentPayload);
        console.log('Updated RA state assignment:', assignmentId);
        toast.success(`Updated registered agent for ${selectedStateForAgent}`);
      } else {
        // Create new assignment
        await api.createRAStateAssignment(assignmentPayload);
        console.log('Created new RA state assignment');
        toast.success(`Added registered agent for ${selectedStateForAgent}`);
      }
      
      // Step 4: Update local state for immediate UI feedback
      setRegisteredAgentData(prev => ({
        ...prev,
        [selectedStateForAgent]: agentData
      }));
      
      // Step 5: Auto-create expense if fee exists
      const feeAmount = agentData.fee || agentData.annualFee;
      if (feeAmount && parseFloat(feeAmount) > 0) {
        try {
          const today = new Date().toISOString().split('T')[0];
          const expensePayload = {
            entity_id: 'ENT-LOSSPAY-001',
            title: `Registered Agent - ${agentData.agentName}`,
            description: `Annual registered agent service fee for ${agentData.agentName} in ${selectedStateForAgent}`,
            state: selectedStateForAgent,
            category: 'Registered Agent',
            vendor: agentData.agentName,
            amount: parseFloat(feeAmount),
            payment_date: agentData.appointedDate ? convertToISODate(agentData.appointedDate) : today,
            status: 'paid'
          };
          await api.createExpense(expensePayload);
          console.log('Expense created for agent fee:', expensePayload);
        } catch (expenseError) {
          console.error('Failed to create expense entry:', expenseError);
          // Don't fail the agent save if expense fails
        }
      }
      
      // Step 6: Reload compliance data to reflect changes
      await loadRequirements(true); // Preserve open states
      
      // Close the dialog
      setAgentDialogOpen(false);
      setSelectedStateForAgent(null);
      
    } catch (error) {
      console.error('Failed to save registered agent:', error);
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    }
  };

  // Helper function to convert MM/DD/YYYY to ISO format
  const convertToISODate = (mmddyyyy) => {
    if (!mmddyyyy || mmddyyyy.length !== 10) return null;
    const [month, day, year] = mmddyyyy.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleDeleteLicense = async (license, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    setConfirmDialog({
      open: true,
      title: 'Delete License',
      description: `Are you sure you want to delete the ${license.license_type} license? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await api.deleteLicense(license.license_id);
          await loadRequirements(true); // Preserve open states to prevent scroll
          setConfirmDialog({ ...confirmDialog, open: false });
          
          // Show success toast
          const { toast } = await import('sonner');
          toast.success('License deleted successfully');
        } catch (error) {
          console.error('Failed to delete license:', error);
          setConfirmDialog({ ...confirmDialog, open: false });
          
          // Show error toast with specific message
          const { toast } = await import('sonner');
          const errorMessage = error.message || 'Failed to delete license';
          toast.error(errorMessage);
        }
      }
    });
  };

  const handleDeleteRegistration = async (registration, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const isFilingType = registration.filing_type !== undefined;
    const displayName = registration.dba_name || registration.filing_type || 'this item';
    const itemType = isFilingType ? 'Filing' : 'Registration';
    
    setConfirmDialog({
      open: true,
      title: `Delete ${itemType}`,
      description: `Are you sure you want to delete "${displayName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          // Call appropriate delete endpoint based on type
          if (isFilingType) {
            await api.deleteFiling(registration.filing_id);
          } else {
            await api.deleteRegistration(registration.dba_id);
          }
          
          await loadRequirements(true); // Preserve open states to prevent scroll
          setConfirmDialog({ ...confirmDialog, open: false });
          
          // Show success toast
          const { toast } = await import('sonner');
          toast.success(`${itemType} deleted successfully`);
        } catch (error) {
          console.error(`Failed to delete ${itemType.toLowerCase()}:`, error);
          setConfirmDialog({ ...confirmDialog, open: false });
          
          // Show error toast with specific message
          const { toast } = await import('sonner');
          const errorMessage = error.message || `Failed to delete ${itemType.toLowerCase()}`;
          toast.error(errorMessage);
        }
      }
    });
  };

  const handleDeleteAgent = (state, agentName, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    setConfirmDialog({
      open: true,
      title: 'Remove Registered Agent',
      description: `Are you sure you want to remove "${agentName}" as the registered agent for ${state}?`,
      onConfirm: async () => {
        try {
          // Get state code from state name
          const stateCode = Object.keys(stateCodeToName).find(
            code => stateCodeToName[code] === state
          );
          
          if (!stateCode) {
            throw new Error('Invalid state');
          }
          
          // Find and delete the state assignment
          const stateAssignments = await api.getRAStateAssignments({ state: stateCode });
          
          if (stateAssignments.assignments && stateAssignments.assignments.length > 0) {
            const assignmentId = stateAssignments.assignments[0].assignment_id;
            await api.deleteRAStateAssignment(assignmentId);
            toast.success(`Removed registered agent from ${state}`);
          }
          
          // Update local state
          setRegisteredAgentData(prev => {
            const newData = { ...prev };
            delete newData[state];
            return newData;
          });
          
          setConfirmDialog({ ...confirmDialog, open: false });
          
          // Reload compliance data
          await loadRequirements(true); // Preserve open states
          
        } catch (error) {
          console.error('Failed to delete registered agent:', error);
          toast.error(`Failed to remove agent: ${error.message || 'Unknown error'}`);
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };

  const handleLicenseSuccess = () => {
    loadRequirements(true);  // Reload from single source of truth, preserve open states
  };

  const handleRegistrationSuccess = () => {
    loadRequirements(true);  // Reload from single source of truth, preserve open states
  };

  const getExistingStates = () => {
    return getStateList();
  };

  const toggleState = (state) => {
    setOpenStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
  };

  const collapseAll = () => {
    const newOpenStates = {};
    Object.keys(openStates).forEach(state => {
      newOpenStates[state] = false;
    });
    setOpenStates(newOpenStates);
  };

  const expandAll = () => {
    const newOpenStates = {};
    Object.keys(openStates).forEach(state => {
      newOpenStates[state] = true;
    });
    setOpenStates(newOpenStates);
  };

  const areAllExpanded = () => {
    return Object.values(openStates).every(isOpen => isOpen === true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="state-matrix-container">
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">State Compliance Matrix</h1>
            <p className="text-muted-foreground mt-2">Manage licenses, registrations, and compliance requirements across all jurisdictions</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button 
              onClick={areAllExpanded() ? collapseAll : expandAll} 
              variant="outline" 
              size="sm"
              data-testid="toggle-all-states-button"
              className="flex-1 sm:flex-initial"
            >
              {areAllExpanded() ? 'Collapse All' : 'Expand All'}
            </Button>
            <Button onClick={() => setAddStateDialogOpen(true)} size="lg" data-testid="add-state-button" className="flex-1 sm:flex-initial">
              <Plus className="h-4 w-4 mr-2" />
              Add State
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <label className="text-sm font-medium">Filter by State:</label>
          <Select value={selectedState} onValueChange={setSelectedState} data-testid="state-filter-select">
            <SelectTrigger className="w-full sm:w-80">
              <SelectValue placeholder="All States" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {getStateList().map(state => {
                const compliance = getComplianceByState(state);
                const totalCount = (compliance?.counts?.licenses || 0) + (compliance?.counts?.registrations || 0);
                return (
                  <SelectItem key={state} value={state}>
                    {state} ({totalCount})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4">
        {detailedStates.map((state) => {
          const stateLicenses = getLicensesByState(state);
          const stateRegistrations = getRegistrationsByState(state);
          const compliance = getComplianceByState(state);
          
          if (!compliance) return null;  // Skip states without compliance data
          
          // Use compliance data counts (SINGLE SOURCE OF TRUTH)
          const licenseCount = compliance.counts?.licenses || 0;
          const registrationCount = compliance.counts?.registrations || 0;
          const registeredAgentCount = compliance.counts?.registered_agents || 0;
          const totalCount = licenseCount + registrationCount + registeredAgentCount;
          
          const isOpen = openStates[state] || false;
          
          return (
            <Collapsible
              key={state}
              open={isOpen}
              onOpenChange={() => toggleState(state)}
            >
              <Card 
                ref={(el) => (stateCardRefs.current[state] = el)}
                className="transition-all" 
                data-testid={`state-card-${state.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/5 transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                        <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base">{state}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5 flex-wrap">
                          <span>{licenseCount} license{licenseCount !== 1 ? 's' : ''}</span>
                          <span>•</span>
                          <span>{registrationCount} registration{registrationCount !== 1 ? 's' : ''}</span>
                          <span>•</span>
                          {registeredAgentData[state] && Object.values(registeredAgentData[state]).some(val => val) ? (
                            <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                              <Check className="h-3.5 w-3.5" />
                              Registered Agent
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                              <X className="h-3.5 w-3.5" />
                              No Agent
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="bg-white dark:bg-gray-800 text-black dark:text-white border">
                        {totalCount}
                      </Badge>
                      {isOpen ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-4">
                    <div className="space-y-6 border-t pt-4">
                      {/* Compliance Status Section - First Item */}
                      {(() => {
                        const stateCode = stateNameToCode[state];
                        const compliance = stateCode ? complianceData[stateCode] : null;
                        
                        if (!compliance) return null;
                        
                        const getStatusBadgeVariant = (statusColor) => {
                          switch(statusColor) {
                            case 'red': return 'destructive';
                            case 'yellow': return 'default';
                            case 'green': return 'success';
                            case 'gray':
                            default: return 'outline';
                          }
                        };
                        
                        const getStatusIcon = (statusColor) => {
                          switch(statusColor) {
                            case 'red': return <AlertCircle className="h-4 w-4" />;
                            case 'yellow': return <Clock className="h-4 w-4" />;
                            case 'green': return <CheckCircle className="h-4 w-4" />;
                            case 'gray':
                            default: return <AlertTriangle className="h-4 w-4" />;
                          }
                        };
                        
                        return (
                          <div className="pb-6 border-b" data-testid={`compliance-status-${state.replace(/\s+/g, '-').toLowerCase()}`}>
                            <div className="flex items-center gap-2 mb-4">
                              {getStatusIcon(compliance.status_color)}
                              <h4 className="font-semibold text-sm">Compliance Status</h4>
                            </div>
                            
                            {/* Overall Status */}
                            <div className="mb-4">
                              <Badge 
                                variant={getStatusBadgeVariant(compliance.status_color)}
                                className="text-sm px-3 py-1"
                                data-testid={`status-badge-${state.replace(/\s+/g, '-').toLowerCase()}`}
                              >
                                {compliance.status_label}
                              </Badge>
                            </div>
                            
                            {/* Reason Summary */}
                            {compliance.reason_summary && (
                              <div className="mb-4 p-3 rounded-md bg-muted/50">
                                <p className="text-sm text-muted-foreground italic">
                                  {compliance.reason_summary}
                                </p>
                              </div>
                            )}
                            
                            {/* Required Licenses */}
                            {compliance.required_licenses && compliance.required_licenses.length > 0 && (
                              <div className="mb-4 p-3 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                  <span className="text-xs font-semibold text-blue-900 dark:text-blue-100">Required for Compliance</span>
                                </div>
                                <ul className="space-y-1.5">
                                  {compliance.required_licenses.map((lic, idx) => (
                                    <li key={idx} className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400 mt-1.5 flex-shrink-0" />
                                      <span>{lic}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Missing Items (Critical) */}
                            {compliance.missing_items && compliance.missing_items.length > 0 && (
                              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                  <span className="text-xs font-semibold text-red-900 dark:text-red-100">Missing Requirements</span>
                                </div>
                                <ul className="space-y-1.5">
                                  {compliance.missing_items.map((item, idx) => (
                                    <li key={idx} className="text-sm text-red-800 dark:text-red-200 font-medium flex items-start gap-2">
                                      <span className="text-red-600 dark:text-red-400">✗</span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Critical Details */}
                            {compliance.critical_details && compliance.critical_details.length > 0 && (
                              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                  <span className="text-xs font-semibold text-red-900 dark:text-red-100">Critical Issues ({compliance.critical_details.length})</span>
                                </div>
                                <ul className="space-y-1.5">
                                  {compliance.critical_details.slice(0, 5).map((issue, idx) => (
                                    <li key={idx} className="text-sm text-red-800 dark:text-red-200">
                                      • {issue.reason}
                                    </li>
                                  ))}
                                  {compliance.critical_details.length > 5 && (
                                    <li className="text-sm text-red-600 dark:text-red-400 font-medium">
                                      +{compliance.critical_details.length - 5} more issue(s)
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            {/* Upcoming Items (Action Required) */}
                            {compliance.upcoming_items && compliance.upcoming_items.length > 0 && (
                              <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                                  <span className="text-xs font-semibold text-amber-900 dark:text-amber-100">Upcoming Deadlines</span>
                                </div>
                                <ul className="space-y-1.5">
                                  {compliance.upcoming_items.slice(0, 5).map((item, idx) => (
                                    <li key={idx} className="text-sm text-amber-800 dark:text-amber-200">
                                      • {item.reason}
                                    </li>
                                  ))}
                                  {compliance.upcoming_items.length > 5 && (
                                    <li className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                                      +{compliance.upcoming_items.length - 5} more deadline(s)
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            {/* Texas HB 2503 Foreign Registration Exception - Visually Distinct */}
                            {compliance.hb2503_exception && compliance.hb2503_exception.tx_nonresident_agency_hb2503_applies && (
                              <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                                  <span className="text-xs font-semibold text-green-900 dark:text-green-100">HB 2503 - Foreign Registration Exception</span>
                                </div>
                                <p className="text-sm text-green-800 dark:text-green-200 mb-2">
                                  <strong>Status:</strong> Texas Secretary of State foreign corporation registration not required for nonresident agency licensing (HB 2503, effective 09/01/2011)
                                </p>
                                <p className="text-sm text-green-700 dark:text-green-300 italic">
                                  {compliance.hb2503_exception.tx_foreign_registration_exception_basis}
                                </p>
                                {compliance.hb2503_exception.hb2503_review_date && (
                                  <p className="text-xs text-green-600 dark:text-green-400 mt-2 pt-2 border-t border-green-200 dark:border-green-800">
                                    Last reviewed: {compliance.hb2503_exception.hb2503_review_date} | 
                                    By: {compliance.hb2503_exception.hb2503_confirmed_by || 'Compliance Team'}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Registered Agent Section - Shown First if Exists */}
                      {registeredAgentData[state] && Object.values(registeredAgentData[state]).some(val => val) && (
                    <div className="pb-4 border-b">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-emerald-500" />
                          <h4 className="font-semibold text-sm">Registered Agent</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleAddAgent(state, e)}
                            className="h-7 text-xs"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteAgent(state, registeredAgentData[state]?.agentName, e)}
                            className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>

                      <div className="p-4 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {registeredAgentData[state]?.agentName && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Agent Name</p>
                              <p className="text-sm font-medium">{registeredAgentData[state].agentName}</p>
                            </div>
                          )}
                          {registeredAgentData[state]?.agentType && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Agent Type</p>
                              <p className="text-sm font-medium">{registeredAgentData[state].agentType}</p>
                            </div>
                          )}
                        </div>

                        {registeredAgentData[state]?.address && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Address</p>
                            <p className="text-sm">{registeredAgentData[state].address}</p>
                            {(registeredAgentData[state]?.city || registeredAgentData[state]?.agentState || registeredAgentData[state]?.zip) && (
                              <p className="text-sm">
                                {registeredAgentData[state].city && <>{registeredAgentData[state].city}, </>}
                                {registeredAgentData[state].agentState} {registeredAgentData[state].zip}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {registeredAgentData[state]?.phone && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Phone</p>
                              <p className="text-sm">{registeredAgentData[state].phone}</p>
                            </div>
                          )}
                          {registeredAgentData[state]?.email && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Email</p>
                              <p className="text-sm">{registeredAgentData[state].email}</p>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-emerald-200/50 dark:border-emerald-900/50">
                          {registeredAgentData[state]?.appointedDate && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                Appointment Date
                              </p>
                              <p className="text-sm font-medium">{registeredAgentData[state].appointedDate}</p>
                            </div>
                          )}
                          {registeredAgentData[state]?.annualFee && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Annual Fee</p>
                              <p className="text-sm font-medium">${parseFloat(registeredAgentData[state].annualFee).toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Licenses Section */}
                  {stateLicenses.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <h4 className="font-semibold text-sm">Active Licenses ({stateLicenses.length})</h4>
                      </div>
                      <div className="space-y-2">
                        {stateLicenses.map((license) => {
                          const daysUntil = getDaysUntil(license.expiration_date);
                          return (
                            <div 
                              key={license.license_id}
                              className="p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors group cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLicense(license);
                                setEditLicenseDialogOpen(true);
                              }}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="font-medium text-sm">{license.license_type}</div>
                                    {license.category && (
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs bg-white text-black border-border hover:bg-white hover:text-black dark:bg-white dark:text-black dark:hover:bg-white"
                                      >
                                        {license.category}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {license.license_number} • Expires: {formatDate(license.expiration_date)}
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Badge variant={license.status === 'active' ? 'default' : license.status === 'pending_renewal' ? 'secondary' : 'destructive'} className="text-xs">
                                      {license.status.replace('_', ' ')}
                                    </Badge>
                                    <Badge variant={daysUntil <= 30 ? 'destructive' : daysUntil <= 60 ? 'default' : 'secondary'} className="text-xs">
                                      {daysUntil} days
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => handleDeleteLicense(license, e)}
                                  title="Delete license"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* No Licenses - Show Empty State */}
                  {stateLicenses.length === 0 && (
                    <div className="p-4 rounded-lg border border-dashed border-border bg-muted/30">
                      <div className="text-center">
                        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No licenses added for this state</p>
                      </div>
                    </div>
                  )}

                  {/* Ancillary Registrations Section (DBAs + Filings) - SEPARATE from Registered Agents */}
                  {(stateRegistrations.length > 0 || stateLicenses.length > 0) && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center gap-2 mb-3">
                        <Building className="h-4 w-4 text-purple-500" />
                        <h4 className="font-semibold text-sm">Registrations ({stateRegistrations.length})</h4>
                      </div>
                      
                      {stateRegistrations.length > 0 ? (
                        <div className="space-y-2">
                          {stateRegistrations.map((registration) => {
                            const daysUntil = getDaysUntil(registration.expiration_date);
                            const isFilingType = registration.filing_type !== undefined;
                            const displayName = registration.dba_name || registration.filing_type || 'Unknown';
                            const registrationType = registration.registration_type || (isFilingType ? 'Filing' : 'DBA');
                            
                            return (
                              <div 
                                key={registration.dba_id || registration.filing_id}
                                className="p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors group cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isFilingType) {
                                    setSelectedDBA(registration);
                                    setEditDBADialogOpen(true);
                                  }
                                }}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <div className="font-medium text-sm">{displayName}</div>
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs bg-white text-black border-border hover:bg-white hover:text-black dark:bg-white dark:text-black dark:hover:bg-white"
                                      >
                                        {registrationType}
                                      </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground space-y-0.5">
                                      {registration.registration_number && (
                                        <div className="flex items-center gap-1">
                                          <Hash className="h-3 w-3" />
                                          {registration.registration_number}
                                        </div>
                                      )}
                                      {registration.filing_number && (
                                        <div className="flex items-center gap-1">
                                          <Hash className="h-3 w-3" />
                                          {registration.filing_number}
                                        </div>
                                      )}
                                      {registration.registered_agent && (
                                        <div>Agent: {registration.registered_agent}</div>
                                      )}
                                      {registration.agent_address && (
                                        <div className="text-xs">{registration.agent_address}</div>
                                      )}
                                      {registration.registration_date && registration.expiration_date && (
                                        <div>Registered: {formatDate(registration.registration_date)} • Expires: {formatDate(registration.expiration_date)}</div>
                                      )}
                                      {registration.filing_date && (
                                        <div>Filed: {formatDate(registration.filing_date)}</div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant={registration.status === 'active' || registration.status === 'current' ? 'default' : 'secondary'} className="text-xs">
                                        {registration.status || 'current'}
                                      </Badge>
                                      {registration.expiration_date && daysUntil !== null && (
                                        <Badge variant={daysUntil <= 30 ? 'destructive' : daysUntil <= 60 ? 'default' : 'secondary'} className="text-xs">
                                          {daysUntil} days
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={(e) => handleDeleteRegistration(registration, e)}
                                    title={`Delete ${isFilingType ? 'filing' : 'registration'}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="p-4 rounded-lg border border-dashed border-border bg-muted/30">
                          <div className="text-center">
                            <Building className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                            <p className="text-xs text-muted-foreground">No ancillary registrations for this state</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons at Bottom */}
                  <div className="flex flex-wrap items-center gap-2 mt-6 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddLicense(state, e);
                      }}
                      className="h-8 text-xs"
                      data-testid={`add-license-bottom-${state.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add License
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddRegistration(state, e);
                      }}
                      className="h-8 text-xs"
                      data-testid={`add-registration-bottom-${state.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add Ancillary Registration
                    </Button>
                    
                    {(!registeredAgentData[state] || !Object.values(registeredAgentData[state]).some(val => val)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddAgent(state, e);
                        }}
                        className="h-8 text-xs"
                      >
                        <User className="h-3.5 w-3.5 mr-1" />
                        Add Registered Agent
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      );
    })}
  </div>

      <AddStateWizard
        open={addStateDialogOpen}
        onClose={() => setAddStateDialogOpen(false)}
        onSuccess={loadRequirements}
        existingStates={getExistingStates()}
      />

      <AddLicenseDialog
        open={addLicenseDialogOpen}
        onClose={() => {
          setAddLicenseDialogOpen(false);
          setSelectedStateForLicense(null);
        }}
        onSuccess={handleLicenseSuccess}
        state={selectedStateForLicense}
      />

      <AddRegistrationDialog
        open={addRegistrationDialogOpen}
        onClose={() => {
          setAddRegistrationDialogOpen(false);
          setSelectedStateForRegistration(null);
        }}
        onSuccess={handleRegistrationSuccess}
        state={selectedStateForRegistration}
      />

      <AddRegisteredAgentDialog
        open={agentDialogOpen}
        onClose={() => {
          setAgentDialogOpen(false);
          setSelectedStateForAgent(null);
        }}
        onSave={handleSaveAgent}
        state={selectedStateForAgent}
        existingData={registeredAgentData[selectedStateForAgent]}
      />

      <EditLicenseDialog
        license={selectedLicense}
        open={editLicenseDialogOpen}
        onClose={() => {
          setEditLicenseDialogOpen(false);
          setSelectedLicense(null);
        }}
        onSuccess={async () => {
          setEditLicenseDialogOpen(false);
          setSelectedLicense(null);
          await loadRequirements(true); // Preserve open states
        }}
      />

      <EditDBADialog
        dba={selectedDBA}
        open={editDBADialogOpen}
        onClose={() => {
          setEditDBADialogOpen(false);
          setSelectedDBA(null);
        }}
        onSuccess={async () => {
          setEditDBADialogOpen(false);
          setSelectedDBA(null);
          await loadRequirements(true); // Preserve open states
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
};

export default StateMatrix;
